import { useEffect } from 'react';

import { useSeriesPrep } from '../hooks/useSeriesPrep';
import './BatchSummaryCard.css';

interface Props {
  batchId: string;
  /** The 202's ``total`` — shown as the denominator immediately, before
   *  the first poll lands, so the card never flashes «готовлю 0/0». */
  total: number;
  /** Called once the batch leaves ``PREPARING`` (DONE or PARTIAL). The
   *  parent forgets the batch (registry + this card) and invalidates the
   *  batches/jobs queries so the freshly-built series surface in the
   *  normal Feed. */
  onDone: (batchId: string) => void;
}

/** Wave 4b — «готовлю N/M» card for an async-convergence batch whose
 *  jobs are still being built by the scheduler daemon. Bridges the gap
 *  between the instant 202 and the moment the series show up in the
 *  /batches poll, so the operator gets immediate, honest feedback that
 *  their submit was accepted and is in progress.
 *
 *  Visual language matches {@link BatchSummaryCard} (same progress bar)
 *  so the prep card reads as «a batch that isn't quite a batch yet». */
export function SeriesPrepCard({ batchId, total, onDone }: Props) {
  const { data } = useSeriesPrep(batchId);

  const status = data?.status ?? 'PREPARING';
  const prepared = data?.prepared ?? 0;
  const failed = data?.failed ?? 0;
  const denom = data?.total ?? total;
  const terminal = prepared + failed;
  const percent = denom > 0 ? Math.round((terminal / denom) * 100) : 0;

  // Leave PREPARING → hand the batch back to the parent. Done in an
  // effect (not during render) so the registry mutation + query
  // invalidation happen after commit, not mid-render.
  useEffect(() => {
    if (status !== 'PREPARING') onDone(batchId);
  }, [status, batchId, onDone]);

  // Already terminal on first paint (e.g. a reload after the daemon
  // finished): render nothing — the effect above clears it immediately.
  if (status !== 'PREPARING') return null;

  return (
    <div
      className="batch-summary-card"
      data-testid="series-prep-card"
      aria-label={`Готовлю серии: ${prepared} из ${denom}`}
    >
      <div className="batch-tag">⏳ Готовлю серии…</div>
      <div className="batch-meta">
        принято, считаю окна и нарезаю RINEX в фоне
      </div>

      <div className="batch-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="progress-text">
          {prepared} из {denom} готово
        </div>
      </div>

      <div className="batch-counts">
        {prepared > 0 && <span className="count-done">✅ {prepared}</span>}
        {failed > 0 && <span className="count-failed">❌ {failed}</span>}
        {(data?.running ?? 0) > 0 && (
          <span className="count-active">🔄 {data?.running}</span>
        )}
        {(data?.queued ?? 0) > 0 && (
          <span className="count-pending">⏳ {data?.queued}</span>
        )}
      </div>
    </div>
  );
}
